export const EDITIONS = [
  "Janambhoomi",
  "Phulchhab",
  "Kutchmitra",
  "Pravasi",
  "Vyapar"
];

// Attribution label shown as an article's "Source". Mirrors
// server/src/config/editions.js — the server is authoritative and stamps the
// stored value; this copy only powers the read-only preview in the admin form
// so the admin sees what will be saved. The internal edition key stays
// "Janambhoomi"; only the label reads "Janmabhoomi".
export const EDITION_SOURCE_LABELS = {
  Janambhoomi: "Janmabhoomi",
  Phulchhab: "Phulchhab",
  Kutchmitra: "Kutchmitra",
  Pravasi: "Pravasi",
  Vyapar: "Vyapar"
};

export const editionSourceLabel = (edition) => {
  const key = (edition || "").toString().trim();
  if (!key) return "";
  return EDITION_SOURCE_LABELS[key] || key;
};

// Categories are managed on the Categories page (GET /api/categories). These
// lists are only a fallback for the article form's dropdowns if that request
// fails.
//
// Mirrors server/src/config/newsCategories.js. Section categories first (same
// order as the app's category strip), then the older categories that existing
// articles are still stored under — those must stay selectable so editing an
// older article doesn't fail validation.
export const NEWS_SECTION_CATEGORIES = [
  "top",
  "gujarat",
  "kutch",
  "saurashtra",
  "mumbai",
  "business",
  "india",
  "world",
  "politics",
  "crime",
  "sports",
  "entertainment",
  "lifestyle",
  "religion",
  "astrology",
  "editorial",
  "latest_news",
  "special",
  "social",
  "obituary",
  "event"
];

export const LEGACY_NEWS_CATEGORIES = [
  "breaking_news",
  "technology",
  "health",
  "education"
];

// Deduped: every slug appears exactly once across the two lists.
export const NEWS_CATEGORIES = [
  ...new Set([...NEWS_SECTION_CATEGORIES, ...LEGACY_NEWS_CATEGORIES])
];

const NEWS_CATEGORY_LABELS = {
  top: "Top News",
  gujarat: "Gujarat",
  kutch: "Kutch (Kutchmitra)",
  saurashtra: "Saurashtra (Phulchhab)",
  mumbai: "Mumbai (Janmabhoomi Pravasi)",
  business: "Business (Vyapar)",
  india: "India",
  world: "World",
  politics: "Politics",
  crime: "Crime",
  sports: "Sports",
  entertainment: "Entertainment",
  lifestyle: "Lifestyle",
  religion: "Religion / Dharma",
  astrology: "Astrology / Panchang",
  editorial: "Editorial / Opinion",
  latest_news: "Latest News",
  main_news: "Main News", // legacy: not selectable, label kept for old rows
  special: "Janmabhoomi Special",
  social: "Social",
  obituary: "Obituary",
  event: "Event",
  breaking_news: "Breaking News",
  technology: "Technology",
  health: "Health",
  education: "Education"
};

/** Human-readable label for a category slug, for the admin dropdowns. */
export const newsCategoryLabel = (category) => {
  const key = (category || "").toString().trim().toLowerCase();
  if (!key) return "";
  return (
    NEWS_CATEGORY_LABELS[key] ||
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

export const ADMIN_MODULES = [
  {
    id: "janmabhoomi-news",
    kind: "news",
    logo: "JANMABHOOMI",
    basePath: "/janmabhoomi_admin",
    mainPath: "/janmabhoomi_admin/manage_news",
    pageTitle: "Manage News",
    primaryActionLabel: "Add News",
    defaultEdition: "Janambhoomi",
    navItems: [
      { label: "Dashboard", path: "/janmabhoomi_admin/dashboard" },
      { label: "News Articles", path: "/janmabhoomi_admin/manage_news" },
      // Categories are shared by every edition and the whole mobile app, so
      // they're managed in one place, here.
      { label: "Categories", path: "/janmabhoomi_admin/categories" },
      { label: "Subscribers Count", path: "/janmabhoomi_admin/subscribers_count" },
      { label: "E-Paper Admin", path: "/epaper_admin/epaper" }
    ]
  },
  {
    id: "pravasi-news",
    kind: "news",
    logo: "PRAVASI",
    basePath: "/pravasi_admin",
    mainPath: "/pravasi_admin/manage_news",
    pageTitle: "Manage News",
    primaryActionLabel: "Add News",
    defaultEdition: "Pravasi",
    navItems: [
      { label: "Dashboard", path: "/pravasi_admin/dashboard" },
      { label: "News Articles", path: "/pravasi_admin/manage_news" },
      { label: "Hashtags", path: "/pravasi_admin/hashtags" },
      { label: "Users", path: "/pravasi_admin/users" }
    ]
  },
  {
    id: "vyapar-gujarati-news",
    kind: "news",
    logo: "VYAPAR GUJARATI",
    basePath: "/vyapar_gujarati_admin",
    mainPath: "/vyapar_gujarati_admin/manage_news",
    pageTitle: "Manage News",
    primaryActionLabel: "Add News",
    defaultEdition: "Vyapar",
    navItems: [
      { label: "Dashboard", path: "/vyapar_gujarati_admin/dashboard" },
      { label: "News Articles", path: "/vyapar_gujarati_admin/manage_news" },
      { label: "Hashtags", path: "/vyapar_gujarati_admin/hashtags" },
      { label: "Users", path: "/vyapar_gujarati_admin/users" }
    ]
  },
  {
    id: "vyapar-hindi-news",
    kind: "news",
    logo: "VYAPAR HINDI",
    basePath: "/vyapar_hindi_admin",
    mainPath: "/vyapar_hindi_admin/manage_news",
    pageTitle: "Manage News",
    primaryActionLabel: "Add News",
    defaultEdition: "Vyapar",
    navItems: [
      { label: "Dashboard", path: "/vyapar_hindi_admin/dashboard" },
      { label: "News Articles", path: "/vyapar_hindi_admin/manage_news" },
      { label: "Hashtags", path: "/vyapar_hindi_admin/hashtags" },
      { label: "Daily E-Paper", path: "/vyapar_hindi_admin/daily_epaper" }
    ]
  },
  {
    id: "janmabhoomi-epaper",
    kind: "epaper",
    logo: "JANMABHOOMI E-PAPER",
    basePath: "/epaper_admin",
    mainPath: "/epaper_admin/epaper",
    pageTitle: "E-Paper",
    primaryActionLabel: "Create E-Paper",
    navItems: [
      { label: "Dashboard", path: "/epaper_admin/dashboard" },
      { label: "E-Paper", path: "/epaper_admin/epaper" },
      { label: "Subscriptions", path: "/epaper_admin/subscriptions" },
      { label: "Subscription Pricing", path: "/epaper_admin/subscription_pricing" },
      { label: "Special Plan Requests", path: "/epaper_admin/special_plan_requests" },
      { label: "Users", path: "/epaper_admin/users" }
    ]
  },
  {
    id: "vyapar-epaper",
    kind: "epaper",
    logo: "VYAPAR E-PAPER",
    basePath: "/vyapar_epaper_admin",
    mainPath: "/vyapar_epaper_admin/epaper",
    pageTitle: "E-Paper",
    primaryActionLabel: "Create E-Paper",
    navItems: [
      { label: "Dashboard", path: "/vyapar_epaper_admin/dashboard" },
      { label: "E-Paper", path: "/vyapar_epaper_admin/epaper" },
      { label: "Subscriptions", path: "/vyapar_epaper_admin/subscriptions" },
      { label: "Users", path: "/vyapar_epaper_admin/users" }
    ]
  }
];
